import {
  DermloopClinicalActionVariantEnum,
  ReasonForConsultEnum,
} from '@dermloop/static-values';
import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { CreateClinicalActionOccurrenceDto } from '../forms/clinical-action.dtos';
import { BiopsyDto } from '../clinical-actions/biopsy.dtos';
import { DismissalDto } from '../clinical-actions/dimissal.dtos';
import type { FollowUpRequestDto } from '../clinical-actions/follow-up-request.dtos';
import type { ReferralDto } from '../clinical-actions/referral.dtos';
import { TopicalTreatmentDto } from '../clinical-actions/topical-treatment.dtos';
import { FieldConfigurationDto } from '../field-configuration/field-configuration.dto';
import { ConditionDetailsDto } from '../condition-info/condition-details.dtos';
import { ConditionImageDto } from '../condition-info/condition-image.dtos';
import type { UserDto } from '../user.dto';
import { ConditionDto } from './condition.dtos';
import { ConditionTriageFormSubmissionDto } from './condition-triage-form-submission.dtos';

export class CreateConditionTriageDto {
  @IsOptional()
  @IsBoolean()
  draft?: boolean;

  @IsOptional()
  @ValidateNested()
  @Type(() => ConditionTriageFormSubmissionDto)
  formSubmission?: ConditionTriageFormSubmissionDto;

  @IsOptional()
  @IsString()
  note?: string;

  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  skinConditionId?: string;

  @IsOptional()
  @IsNotEmpty()
  conditionDetails?: ConditionDetailsDto;

  /** @deprecated Use conditionDetails */
  @IsOptional()
  @IsNotEmpty()
  lesionDetails?: ConditionDetailsDto;

  @IsOptional()
  @ValidateNested()
  overviewImage?: ConditionImageDto;

  @IsOptional()
  @ValidateNested()
  closeUpImage?: ConditionImageDto;

  @IsOptional()
  @ValidateNested()
  dermoscopicImage?: ConditionImageDto;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  patientDetailsId?: string;

  // FHIR reference (e.g. "Encounter/123") of the encounter that initiated this
  // triage. Resolved server-side (find-or-create) and linked to the triage so
  // the poll-order-id cron can later resolve the FHIR order for it.
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  encounterFhirReference?: string;

  @IsOptional()
  @IsNotEmpty()
  referralAction?: ReferralDto;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  requestedFollowUp?: FollowUpRequestDto;

  @IsOptional()
  @IsNotEmpty()
  dismissalAction?: DismissalDto;

  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum)
  clinicalAction?: DermloopClinicalActionVariantEnum;

  @IsOptional()
  @IsNotEmpty()
  biopsyAction?: BiopsyDto;

  @IsOptional()
  @IsBoolean()
  plannedBiopsyAction?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @IsBoolean()
  newImageRegistration?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  topicalTreatmentAction?: TopicalTreatmentDto;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  diagnosisId?: string;

  @IsOptional()
  @IsUUID(undefined, { each: true })
  diagnosisGroupIds?: string[];

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  differentialDiagnosisId?: string;

  @IsOptional()
  @IsUUID(undefined, { each: true })
  differentialDiagnosisGroupIds?: string[];

  @IsOptional()
  @IsUUID()
  difficultyId?: string;

  @IsOptional()
  @IsUUID()
  lesionThicknessId?: string;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  dismissalActionId?: string;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  medicalUnitId?: string;

  @IsOptional()
  @IsEnum(ReasonForConsultEnum)
  reasonForConsult?: ReasonForConsultEnum;
}

export class UpdateConditionTriageDto {
  @IsOptional()
  @IsUUID()
  id?: string;

  @IsOptional()
  @IsBoolean()
  draft?: boolean;

  /** An explicit `null` clears the submission the triage owns. */
  @IsOptional()
  @ValidateNested()
  @Type(() => ConditionTriageFormSubmissionDto)
  formSubmission?: ConditionTriageFormSubmissionDto | null;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  note?: string;

  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  skinConditionId?: string;

  @IsOptional()
  @IsNotEmpty()
  conditionDetails?: ConditionDetailsDto;

  /** @deprecated Use conditionDetails */
  @IsOptional()
  @IsNotEmpty()
  lesionDetails?: ConditionDetailsDto;

  @IsOptional()
  @ValidateNested()
  overviewImage?: ConditionImageDto;

  @IsOptional()
  @ValidateNested()
  closeUpImage?: ConditionImageDto;

  @IsOptional()
  @ValidateNested()
  dermoscopicImage?: ConditionImageDto;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  patientDetailsId?: string;

  @IsOptional()
  @IsNotEmpty()
  referralAction?: ReferralDto;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  requestedFollowUp?: FollowUpRequestDto;

  @IsOptional()
  @IsNotEmpty()
  dismissalAction?: DismissalDto;

  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum)
  clinicalAction?: DermloopClinicalActionVariantEnum;

  @IsOptional()
  @IsNotEmpty()
  biopsyAction?: BiopsyDto;

  @IsOptional()
  @IsBoolean()
  plannedBiopsyAction?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @IsBoolean()
  newImageRegistration?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  topicalTreatmentAction?: TopicalTreatmentDto;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  diagnosisId?: string;

  @IsOptional()
  @IsUUID(undefined, { each: true })
  diagnosisGroupIds?: string[];

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  differentialDiagnosisId?: string;

  @IsOptional()
  @IsUUID(undefined, { each: true })
  differentialDiagnosisGroupIds?: string[];

  @IsOptional()
  @IsUUID()
  difficultyId?: string;

  @IsOptional()
  @IsUUID()
  lesionThicknessId?: string;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  dismissalActionId?: string;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  medicalUnitId?: string;

  @IsOptional()
  @IsEnum(ReasonForConsultEnum)
  reasonForConsult?: ReasonForConsultEnum;

  /**
   * The actions completing the initial registration of a form with an action configuration, at
   * least one, created with it. Not accepted for other forms.
   */
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CreateClinicalActionOccurrenceDto)
  actions?: CreateClinicalActionOccurrenceDto[];

  /** The Epic encounter `actions` are taken in; defaults to the triage's own. */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  encounterFhirReference?: string;
}

/**
 * Triage update fields that clients can merge into a fetched {@link ConditionTriageDto}
 * to keep local state in sync. `formSubmission` is excluded because its write shape
 * differs from the persisted one the read model returns.
 */
export type MergeableConditionTriageUpdate = Omit<
  UpdateConditionTriageDto,
  'formSubmission'
>;

export class ConditionTriageDto implements BaseDto {
  @IsUUID()
  id: string;
  createdByUser?: UserDto;
  updatedByUser?: UserDto;
  deletedByUser?: UserDto;
  created?: string | Date;
  updated?: string | Date;
  deleted?: string | Date;

  @IsBoolean()
  draft: boolean;

  @IsOptional()
  @IsBoolean()
  complete?: boolean;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  note?: string;

  @IsUUID()
  @IsNotEmpty()
  conditionId: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  skinConditionId?: string;

  @IsOptional()
  @ValidateNested()
  condition?: ConditionDto;

  @IsOptional()
  @ValidateNested()
  /** @deprecated Use condition */
  skinCondition?: ConditionDto;

  @IsOptional()
  @ValidateNested()
  overviewImage?: ConditionImageDto;

  @IsOptional()
  @ValidateNested()
  closeUpImage?: ConditionImageDto;

  @IsOptional()
  @ValidateNested()
  dermoscopicImage?: ConditionImageDto;

  overviewImages?: ConditionImageDto[];
  closeUpImages?: ConditionImageDto[];
  dermoscopicImages?: ConditionImageDto[];

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  conditionDetails?: ConditionDetailsDto;

  /** @deprecated Use conditionDetails */
  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  lesionDetails?: ConditionDetailsDto;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  patientDetailsId?: string;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  referralAction?: ReferralDto;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  requestedFollowUp?: FollowUpRequestDto;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  dismissalAction?: DismissalDto;

  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum)
  clinicalAction?: DermloopClinicalActionVariantEnum;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  biopsyAction?: BiopsyDto;

  @IsOptional()
  @IsBoolean()
  plannedBiopsyAction?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @IsBoolean()
  newImageRegistration?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  topicalTreatmentAction?: TopicalTreatmentDto;

  /**
   * The submitted form this triage was answered through, if any. The answers
   * themselves stay off this DTO — a consumer that needs them reads the
   * submission — but whether one exists decides whether the legacy action
   * columns say anything at all, and only the triage itself knows that.
   */
  @IsOptional()
  @IsUUID()
  formSubmissionId?: string;

  @IsNotEmpty()
  @IsString()
  fieldConfigurationId: string;

  @IsOptional()
  @IsNotEmpty()
  @ValidateNested()
  fieldConfiguration?: FieldConfigurationDto;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  diagnosisId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsUUID(undefined, { each: true })
  diagnosisGroupIds?: string[];

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  differentialDiagnosisId?: string;

  @IsOptional()
  @IsUUID(undefined, { each: true })
  differentialDiagnosisGroupIds?: string[];

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  difficultyId?: string;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  lesionThicknessId?: string;

  @IsUUID()
  @IsNotEmpty()
  medicalUnitId: string;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  medicalUnitName?: string;

  @IsOptional()
  @IsUUID()
  @IsNotEmpty()
  healthRecordId?: string;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  ssn?: string;

  @IsOptional()
  @IsBoolean()
  @IsNotEmpty()
  isNewRegistration?: boolean;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  registrationDate?: string;

  @IsOptional()
  @IsString({ each: true })
  validation?: string[];

  @IsOptional()
  @IsEnum(ReasonForConsultEnum)
  reasonForConsult?: ReasonForConsultEnum;
}

export class DeleteConditionTriageResponseDto {
  @IsBoolean()
  success: boolean;

  @IsBoolean()
  deletedEntireCondition: boolean;

  @IsUUID()
  conditionId: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  skinConditionId?: string;
}
